import type { NotificationRecord } from '@/shared/types/notification.types';

export interface NotificationDisplay {
  title: string;
  /** Optional supporting line — empty string if nothing to show. */
  body: string;
}

/**
 * Derives a human-readable (title, body) pair from a NotificationRecord.
 * Record shape varies by `type`:
 *
 *   - PUSH records: rich text usually lives in `pushNotificationResponse.{title,body}`;
 *     older write paths copy it to the top-level `title`/`message` too.
 *   - WhatsApp template-send logs: only carry `templateId` + `phoneNumber`. We
 *     synthesise "Template <name> sent to <phone>" so the bell row is meaningful
 *     instead of just printing the literal string "whatsapp".
 *   - Anything else falls back to whatever top-level fields are present.
 *
 * Without this, a bell entry from an OTP send log rendered as the bare type
 * tag ("whatsapp") with no other info — which was the regression captured
 * in the bug report.
 */
export function deriveNotificationDisplay(n: NotificationRecord): NotificationDisplay {
  const topTitle = (n.title || '').trim();
  const topBody = (n.message || '').trim();
  const pushTitle = (n.pushNotificationResponse?.title || '').trim();
  const pushBody = (n.pushNotificationResponse?.body || '').trim();

  // Push notifications — prefer the FCM payload when the top-level fields
  // are empty.
  if (n.type?.toLowerCase() === 'push') {
    return {
      title: topTitle || pushTitle || 'Notification',
      body: topBody || pushBody
    };
  }

  // WhatsApp template-send logs — synthesise from templateId + phone.
  if (n.type?.toLowerCase() === 'whatsapp') {
    if (topTitle || topBody) return { title: topTitle || 'WhatsApp message', body: topBody };
    const template = n.templateId ? humaniseTemplateName(n.templateId) : 'WhatsApp message';
    const recipient = n.phoneNumber ? `Sent to ${n.phoneNumber}` : '';
    return { title: template, body: recipient };
  }

  // SMS / email / unknown — best-effort.
  return {
    title: topTitle || pushTitle || (n.type ? n.type.toUpperCase() : 'Notification'),
    body: topBody || pushBody
  };
}

/** Translates `LOGIN_OTP_AR` → `Login OTP (AR)` for the bell label. */
function humaniseTemplateName(templateId: string): string {
  // Strip a trailing locale suffix (_EN/_AR/_FR/…).
  const match = templateId.match(/^(.+?)_([A-Z]{2})$/);
  const base = match ? match[1] : templateId;
  const locale = match ? ` (${match[2]})` : '';
  const pretty = base
    .toLowerCase()
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return pretty + locale;
}
